import { useState } from "react";
import { ArrowRight, Mail, User } from "lucide-react";

export default function EmailCapture({ onSubmit }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isFormValid = firstName.trim() && email.trim() && isValidEmail;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    setIsSubmitting(true);
    await onSubmit({ first_name: firstName.trim(), last_name: lastName.trim(), email: email.trim() });
  };

  return (
    <div
      data-testid="email-capture-screen"
      className="min-h-screen bg-[#054452] flex flex-col items-center justify-center px-4 sm:px-6"
    >
      <div className="max-w-[440px] w-full text-center">
        <p className="font-body text-xs tracking-[0.25em] uppercase text-[#F3B21A] mb-4 animate-fade-in-up">
          Your Results Are Ready
        </p>

        <h2
          className="font-display text-2xl sm:text-3xl font-light text-white leading-tight mb-3 animate-fade-in-up-delay-1"
          data-testid="email-headline"
        >
          Before we reveal your <em className="text-[#E66E6D]">identity pattern</em>
        </h2>

        <p className="font-body text-sm text-[#F8D4CE] font-light mb-8 animate-fade-in-up-delay-2">
          Enter your details to unlock your personalized report. We'll also send you a copy.
        </p>

        <form onSubmit={handleSubmit} className="animate-fade-in-up-delay-3">
          {/* First Name */}
          <div className="relative mb-3">
            <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0C6474]" />
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              data-testid="first-name-input"
              className="w-full bg-[#035162] border border-[#0C6474] text-white font-body text-sm px-11 py-3.5 placeholder:text-[#0C6474] focus:outline-none focus:border-[#F3B21A] transition-colors"
            />
          </div>

          {/* Last Name */}
          <div className="relative mb-3">
            <User size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0C6474]" />
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name (optional)"
              data-testid="last-name-input"
              className="w-full bg-[#035162] border border-[#0C6474] text-white font-body text-sm px-11 py-3.5 placeholder:text-[#0C6474] focus:outline-none focus:border-[#F3B21A] transition-colors"
            />
          </div>

          {/* Email */}
          <div className="relative mb-6">
            <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0C6474]" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              data-testid="email-input"
              className="w-full bg-[#035162] border border-[#0C6474] text-white font-body text-sm px-11 py-3.5 placeholder:text-[#0C6474] focus:outline-none focus:border-[#F3B21A] transition-colors"
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            data-testid="continue-to-results-btn"
            className={`inline-flex items-center justify-center gap-3 w-full py-4 font-body font-medium text-sm tracking-[0.15em] uppercase transition-all duration-300 active:scale-[0.98] ${
              isFormValid && !isSubmitting
                ? "bg-[#F3B21A] text-[#054452] hover:bg-[#E66E6D] hover:text-white"
                : "bg-[#035162] text-[#0C6474] cursor-not-allowed"
            }`}
          >
            {isSubmitting ? "Generating your report..." : "Reveal My Results"}
            {!isSubmitting && <ArrowRight size={16} />}
          </button>
        </form>

        <p className="font-body text-[10px] text-[#0C6474] mt-4 animate-fade-in-up-delay-4">
          Your information is private. No spam — just your personalized report.
        </p>
      </div>
    </div>
  );
}
